import React from "react";
import {
  Card,
  DateMetricsCard,
  DateTermCard,
  PivotCard,
} from "@radarly/models/cards";
import {
  isAICardDataviz,
  isColorOptionAvailable,
} from "@radarly/models/cards/dataviz";
import { AICard, MultipleLineChartCard } from "@radarly/models/cards/model";
import {
  Dataviz,
  isCloudDataviz,
  isDoubleTimeChartDataviz,
  isEmvDataviz,
  isHorizontalBarChartDataviz,
  isHorizontalStackedBarChartDataviz,
  isKpiDataviz,
  isLineChartDataviz,
  isMapDataviz,
  isMultipleLineChartDataviz,
  isStackedBarChartDataviz,
  isStreamChartDataviz,
} from "@radarly/models/dataviz";
import DoubleTimeChart from "./double-time-chart";
import Emv from "./emv";
import AI from "./ai";
import HorizontalBarChart from "./horizontal-bar-chart";
import HorizontalStackedBarChart from "./horizontal-stacked-bar-chart";
import Kpi from "./kpi";
import LineChart from "./line-chart";
import MapChart from "./map";
import MultipleLineChart from "./multiple-line-chart";
import StackedBarChart from "./stacked-bar-chart";
import StreamChart from "./stream-chart";
import { PanelType } from "..";

export interface SharedPanelsCardEditionSidebarOptionsProps<D extends Dataviz> {
  dataviz: D;
  setDataviz: (dataviz: D) => void;
  setCard?: (card: Card) => void;
  aiContext: string;
  setAIContext: (description: string) => void;
  card: Card;
  renderColor: (dataviz: D) => React.ReactElement;
  setPanel: (panel: { type: PanelType; index?: number }) => void;
}

export default function SharedPanelsCardEditionSidebarOptions<
  D extends Dataviz
>({
  dataviz,
  setDataviz,
  setCard,
  card,
  aiContext,
  setAIContext,
  renderColor,
  setPanel,
}: SharedPanelsCardEditionSidebarOptionsProps<D>) {
  const allowColor = isColorOptionAvailable(card);

  return isHorizontalBarChartDataviz(dataviz) ? (
    <HorizontalBarChart
      dataviz={dataviz}
      setCard={setCard}
      card={card}
      color={renderColor(dataviz)}
    />
  ) : isKpiDataviz(dataviz) ? (
    <Kpi
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card}
      setCard={setCard}
      color={renderColor(dataviz)}
    />
  ) : isLineChartDataviz(dataviz) ? (
    <LineChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      setCard={setCard}
      card={card}
      color={renderColor(dataviz)}
    />
  ) : isHorizontalStackedBarChartDataviz(dataviz) ? (
    <HorizontalStackedBarChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card as PivotCard}
    />
  ) : isCloudDataviz(dataviz) ? (
    allowColor && renderColor(dataviz)
  ) : isStackedBarChartDataviz(dataviz) ? (
    <StackedBarChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card as DateTermCard}
      setCard={setCard}
    />
  ) : isMapDataviz(dataviz) ? (
    <MapChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card}
      color={renderColor(dataviz)}
    />
  ) : isStreamChartDataviz(dataviz) ? (
    <StreamChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
    />
  ) : isMultipleLineChartDataviz(dataviz) ? (
    <MultipleLineChart
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card as MultipleLineChartCard}
      setCard={setCard}
      setPanel={setPanel}
    />
  ) : isDoubleTimeChartDataviz(dataviz) ? (
    <DoubleTimeChart
      dataviz={dataviz}
      setDataviz={(dataviz) => setDataviz(dataviz as D)}
      card={card as DateMetricsCard}
      setCard={setCard}
    />
  ) : isEmvDataviz(dataviz) ? (
    <Emv card={card} setCard={setCard} />
  ) : isAICardDataviz(dataviz) ? (
    <AI
      card={card as AICard}
      setCard={setCard}
      aiContext={aiContext}
      setAIContext={setAIContext}
    />
  ) : null;
}
